import React, { useState, useEffect } from 'react'
import format from 'date-fns/format'
import { Global, css } from '@emotion/react'
import {
  BlockingLoader,
  Button,
  Container,
  Icon,
  ListContainer,
  Popup,
  State,
  TextBody,
  TextTitle,
  ToastContext,
  Carousel,
  CarouselCards,
  Tools
} from '@pdrj/pmi-iqos-ds-react'
import {
  analytics,
  parseTranslations,
  PublishedComponent,
  CommonTranslations,
  goToRoute,
  Sites,
  Routes,
  ProductCard,
  getDateInTimeZone,
  formatDate,
  LayoutContainer
} from '@pdrj/pmi-iqos-shared-react'
import { IqosphereApiManager } from '@iqosphere-common/services'
import {
  getInitialPropsMock,
  buildQuestInfo,
  buildQuestDetailState,
  buildQuestState,
  questStatusTypes
} from '@iqosphere-common/utils'
import { QuestPreview } from '@iqosphere-common/components'
import QuestListTranslations from './quest-list.json'

const QuestListComponent = ({
  translations,
  questConfigList,
  questProperties,
  campaign,
  alignment,
  line
}) => {
  const [loading, setLoading] = useState(false)
  const [popupContent, setPopupContent] = useState(null)
  const [htmlCardsContent, sethtmlCardsContent] = useState(null)

  const campaignTheme = campaign && campaign.class_names
  const name = campaign && campaign.name
  const questBgColor = campaign && campaign.quest_card_bg_color
  const api = new IqosphereApiManager(line)

  const alignmentValues = {
    left: 'left',
    center: 'center'
  }

  const cardAlignment = () => {
    switch (alignment) {
      case alignmentValues.left:
        return ListContainer.justifyContentValues.left
      case alignmentValues.center:
        return ListContainer.justifyContentValues.center
      default:
        return ListContainer.justifyContentValues.left
    }
  }

  const renderCards = quests => {
    const cards = !Tools.isDesktop()
      ? quests.map((quest, index) => {
          return (
            <ProductCard
              {...quest}
              key={index}
              layout={ProductCard.layouts.rounded}
              questBgColor={questBgColor}
              isQuestFlow
              showVariantList={false}>
              <Container
                py="4"
                pl="4"
                display={Container.displays.flex}
                alignItems={Container.alignItemsValues.end}>
                <Button {...quest.button} />
              </Container>
            </ProductCard>
          )
        })
      : quests
          .reduce((all, one, i) => {
            const ch = Math.floor(i / 4)
            all[ch] = [].concat(all[ch] || [], one)
            return all
          }, [])
          .map((items, index) => {
            return (
              <Container
                fullHeight
                display={Container.displays.flex}
                key={index}
                pl={index ? '2' : '0'}
                pr={index === items.length - 1 ? '0' : '2'}>
                <ListContainer
                  columns={4}
                  xBetween="4"
                  fullHeight
                  fullWidth
                  justifyContent={cardAlignment()}>
                  {items.map((quest, idx) => {
                    return (
                      <Container
                        additionalClasses="carousel-lite"
                        rounded={Container.roundedSizes.xl}
                        bg={Container.backgrounds.white}
                        overflowHidden
                        display={Container.displays.flex}
                        key={idx}
                        fullHeight>
                        <ProductCard
                          {...quest}
                          layout={ProductCard.layouts.rounded}
                          questBgColor={questBgColor}
                          isQuestFlow
                          showVariantList={false}>
                          <Container
                            py="4"
                            pl="4"
                            display={Container.displays.flex}
                            alignItems={Container.alignItemsValues.end}>
                            <Button {...quest.button} />
                          </Container>
                        </ProductCard>
                      </Container>
                    )
                  })}
                </ListContainer>
              </Container>
            )
          })

    if (!cards || !cards.length) return
    return !Tools.isDesktop() ? (
      <CarouselCards cards={cards} />
    ) : (
      <Carousel
        imageSlideSize="100%"
        lite={true}
        showArrowsTopRight
        showArrows={true}
        showIndicators={false}
        infinite={false}>
        {cards}
      </Carousel>
    )
  }

  useEffect(() => {
    async function init() {
      setLoading(true)
      try {
        const res = await api.iqosphereQuest.getQuestList()
        const qList = buildQuestListState(questConfigList, res.quests)
        const renderCardsHTML = renderCards(qList)
        sethtmlCardsContent(renderCardsHTML)
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])

  const popupToast = ToastContext.getContextActions().popupToast

  const clearPopup = () => {
    setPopupContent(null)
  }

  const buildQuestListState = (questConfigList, questList) => {
    const qList = (questConfigList || []).map((config, questIndex) => {
      const quest = questList.find(q => config.k === q.id.toString())
      const currentQuest = quest ? buildQuestState(quest) : {}

      const state =
        currentQuest && currentQuest.status === questStatusTypes.cleared
          ? State.states.valid
          : (currentQuest &&
              currentQuest.status === questStatusTypes.moderation) ||
            (currentQuest &&
              currentQuest.status === questStatusTypes.noticeFailed)
          ? State.states.pending
          : State.states.coming
      const isActionEnabled =
        currentQuest &&
        (currentQuest.status === questStatusTypes.allowed ||
          currentQuest.status === questStatusTypes.cleared)
      const questInfo = buildQuestInfo(config.f, {
        ...questProperties,
        commentBackground: config.i_b,
        id: config.k
      })
      return {
        button: {
          text:
            currentQuest.status === questStatusTypes.closed ||
            currentQuest.status === questStatusTypes.allowed
              ? translations.tk_availableQuest
              : currentQuest.status === questStatusTypes.cleared
              ? translations.tk_viewAction
              : currentQuest.status === questStatusTypes.comingSoon
              ? translations.tk_comingSoon
              : translations.tk_unavailableQuest,
          variant: Button.variants.primary,
          disabled: !isActionEnabled,
          onClickHandler: async () => {
            setLoading(true)
            try {
              analytics.startAvailableQuest(questInfo.editionId, questIndex + 1)
              if (currentQuest.status === questStatusTypes.cleared) {
                const res = await api.iqosphereQuest.getQuestDetail(quest.id)
                const questState = buildQuestDetailState(res)
                setPopupContent({
                  layout: (
                    <QuestPreview
                      translations={translations}
                      questInfo={questInfo}
                      questState={questState}
                    />
                  ),
                  title: config && config.t,
                  subtitle: config && config.h,
                  button: {
                    variant: Button.variants.tertiary,
                    icon: Icon.images.arrowRight,
                    iconPosition: Button.iconPositions.right,
                    text: translations.tk_checkMyQoins,
                    onClickHandler: () => {
                      analytics.checkPointsEvent(config.k, config.e)
                      goToRoute({
                        mainPath: Routes.iqosphereProfileQoinsHistory,
                        site: Sites.iqosphere
                      })
                    }
                  }
                })
              } else {
                goToRoute({
                  mainPath: config && config.u,
                  site: Sites.iqosphere
                })
              }
            } catch {
              popupToast({
                type: ToastContext.types.error,
                title: translations.tk_error,
                subtitle: translations.tk_errorSubtitle
              })
            } finally {
              setLoading(false)
            }
          }
        },
        alternativeBadge: currentQuest &&
          currentQuest.status === questStatusTypes.allowed &&
          config &&
          config.b &&
          config.b.t && {
            title: config.b.t,
            class: config.b.c
          },
        cardOverlay: currentQuest &&
          currentQuest.status !== questStatusTypes.allowed && (
            <Container
              fullHeight
              fullWidth
              display={Container.displays.flex}
              flexDirection={Container.flexDirections.column}
              justifyContent={Container.justifyContentValues.center}
              alignItems={Container.alignItemsValues.center}>
              {currentQuest &&
                currentQuest.status &&
                state !== State.states.coming && (
                  <State size={State.sizes.xl} state={state} />
                )}
              <Container pt="4">
                <TextTitle
                  heading={TextTitle.headings.h5}
                  alignment={TextTitle.alignments.center}
                  darkMode>
                  {currentQuest.status === questStatusTypes.cleared ? (
                    translations.tk_clearState
                  ) : currentQuest.status === questStatusTypes.moderation ||
                    currentQuest.status === questStatusTypes.noticeFailed ? (
                    translations.tk_pendingState
                  ) : (
                    <>
                      <Container>{translations.tk_comingSoon}</Container>
                      <Container>
                        {formatDate(
                          format(
                            getDateInTimeZone(currentQuest.openDate),
                            'yyyy-MM-dd'
                          )
                        )}
                      </Container>
                    </>
                  )}
                </TextTitle>
              </Container>
            </Container>
          ),
        title:
          currentQuest.status === questStatusTypes.comingSoon
            ? ''
            : config && config.lt,
        images: [config && { path: config.i }],
        qoins:
          currentQuest &&
          currentQuest.status === questStatusTypes.allowed &&
          config.q,
        opacity:
          currentQuest && currentQuest.status === questStatusTypes.comingSoon
            ? '100'
            : '80',
        translations
      }
    })
    return qList
  }

  return (
    <Container
      position={Container.positions.relative}
      fullWidth
      additionalClasses={campaignTheme}
      overflowHidden>
      {loading && <BlockingLoader loading />}
      <Global
        styles={css`
          .slider-wrapper,
          .carousel-slider {
            overflow: visible !important;
          }
        `}
      />
      <LayoutContainer fullWidth xMainLayout yMainLayout>
        <Popup
          isOpen={!!popupContent}
          blocking={false}
          fullScreen={false}
          onCloseHandler={clearPopup}>
          {popupContent && (
            <Container
              display={Container.displays.flex}
              alignItems={Container.alignItemsValues.center}
              justifyContent={Container.justifyContentValues.between}
              flexDirection={Container.flexDirections.column}>
              <Container fullWidth>
                <TextTitle
                  alignment={TextTitle.alignments.center}
                  heading={TextTitle.headings.h4}>
                  {popupContent.title}
                </TextTitle>
              </Container>
              {popupContent.title && (
                <Container pt="2" fullWidth>
                  <TextBody
                    alignment={TextTitle.alignments.center}
                    heading={TextTitle.headings.h4}
                    value={popupContent.subtitle}
                  />
                </Container>
              )}

              <Container pt="8" fullHeight fullWidth>
                {popupContent.layout}
              </Container>
              <Container
                py="8"
                fullWidth
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}>
                <Button {...popupContent.button} />
              </Container>
            </Container>
          )}
        </Popup>
        <Container layout={Container.layouts.mid}>
          <Container
            pt={['', '', '8']}
            pb="12"
            fullWidth
            display={Container.displays.flex}
            flexDirection={Container.flexDirections.column}
            alignItems={Container.alignItemsValues.center}>
            <TextTitle
              breakWords
              alignment={TextTitle.alignments.center}
              additionalClasses={campaignTheme}>
              {name}
            </TextTitle>
          </Container>
          {htmlCardsContent && htmlCardsContent}
        </Container>
      </LayoutContainer>
    </Container>
  )
}

const QuestList = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={QuestListComponent}
        {...CompnentDefaultProps}
        {...props}
        fullWidth
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...QuestListTranslations,
  ...CommonTranslations
}

const defaultTranslations = {
  ...parseTranslations(ComponentTranslations, true)
}

const CompnentDefaultProps = {
  componentName: 'QuestList',
  translations: defaultTranslations,
  defaultTranslations: defaultTranslations,
  translationsConfig: ComponentTranslations
}

QuestList.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: CompnentDefaultProps.componentName,
    defaultTranslations: CompnentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: async () => {
      const questConfigList =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('questList')
          : (req.body.extra && req.body.extra.questList) || ''
      const questProperties =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('questProperties')
          : (req.body.extra && req.body.extra.questProperties) || ''
      const campaign =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('campaign')
          : (req.body.extra && req.body.extra.campaign) || ''
      const alignment =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('questListAlignment')
          : (req.body.extra && req.body.extra.alignment) || ''
      return {
        questConfigList,
        questProperties,
        campaign,
        alignment
      }
    }
  })
}

export default QuestList
